import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Loader2,
  Mail
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  addCustomerPreorderMenu,
  removeCustomerPreorderMenu,
  toggleCustomerPreordersComplete,
  updateCustomerPreorderMenu,
  updateCustomerPreorderSettings
} from "../../../../../../APIs/Booking/CustomerPreorderOperations";
import { fetchPreOrderMenus } from "../../../../../../APIs/Venue/GetPreOrderMenus";
import { useAppContext } from "../../../../../../hooks/useAppContext";
import { useDiaryContext } from "../../../../../../hooks/useDiaryContext";
import { useNotificationsContext } from "../../../../../../hooks/useNotificationsContext";
import {
  BookingV4,
  CustomerPreorderMenu
} from "../../../../../../types/bookings";
import {
  isValidServiceChargeInput,
  parseServiceChargeValue
} from "../../../../../../utils/number";
import { EDSButton } from "../../../../../eds/EDSButton";
import { EDSDatePicker } from "../../../../../eds/EDSDatePicker";
import { Alert, AlertDescription } from "../../../../../ui/alert";
import { Input } from "../../../../../ui/input";
import { Label } from "../../../../../ui/label";
import { Switch } from "../../../../../ui/switch";
import SendEmail from "../../Messages/Email/SendEmail";
import {
  getIsSendDisabled,
  getSendDisabledReasons
} from "../../Messages/Email/SendEmailController";
import AddMenuDropdown from "./AddMenuDropdown";
import CustomerPreorderLinks from "./CustomerPreorderLinks";
import CustomerPreorderMenuRow from "./CustomerPreorderMenuRow";

const CustomerPreordersSection: React.FC = () => {
  const diaryContext = useDiaryContext();
  const { venueGroupConfig } = useAppContext();
  const notificationsContext = useNotificationsContext();
  const {
    selectedBookingV4,
    selectedBooking,
    venueConfig,
    venuePreorderMenus,
    setDiaryContext
  } = diaryContext;

  const [isExpanded, setIsExpanded] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingCloseDate, setEditingCloseDate] = useState(false);
  const [closeDateValue, setCloseDateValue] = useState<Date | undefined>(
    undefined
  );
  const [editingServiceCharge, setEditingServiceCharge] = useState(false);
  const [serviceChargeValue, setServiceChargeValue] = useState<string>("0");
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [showDisabledReasons, setShowDisabledReasons] = useState(false);

  const venueId = diaryContext.selectedVenue;

  // Check if venue overrides organisation preorder menus
  const venueOverridesMenus = venueConfig?.override_preorder_menus === true;

  // Use useQuery to fetch venue-specific preorder menus (only when venue overrides)
  const { data: fetchedMenus } = useQuery({
    queryKey: ["GetPreOrderMenus", venueId],
    queryFn: () => fetchPreOrderMenus(venueId),
    enabled: !!venueId && venueOverridesMenus,
    staleTime: 5 * 60 * 1000 // Cache for 5 minutes
  });

  // Determine which menus to use:
  // - If venue overrides: use fetched menus from API (with fallback to org menus if empty)
  // - If venue doesn't override: use organisation-level menus
  const preorderMenus = useMemo(() => {
    if (venueOverridesMenus) {
      // Venue overrides - use API menus, fallback to org menus if empty
      if (fetchedMenus && fetchedMenus.length > 0) {
        return fetchedMenus;
      }
      // Fallback to organisation menus if API returns empty
      return venueGroupConfig?.preorder_menus ?? [];
    }
    // Venue doesn't override - use organisation-level menus
    return venueGroupConfig?.preorder_menus ?? [];
  }, [venueOverridesMenus, fetchedMenus, venueGroupConfig?.preorder_menus]);

  // Update context when menus are determined (only if not already set)
  useEffect(() => {
    if (preorderMenus.length > 0 && !venuePreorderMenus) {
      setDiaryContext({ venuePreorderMenus: preorderMenus });
    }
  }, [preorderMenus, venuePreorderMenus, setDiaryContext]);

  const sendEmailDisabled = getIsSendDisabled({
    venueGroupConfig,
    venueConfig,
    selectedBooking
  });

  const sendEmailDisabledReasons = getSendDisabledReasons({
    venueGroupConfig,
    venueConfig,
    selectedBooking
  });

  const customerPreorderSettings =
    selectedBookingV4?.customer_preorder_settings;
  const menus = customerPreorderSettings?.menus ?? [];
  const isComplete = customerPreorderSettings?.complete ?? false;

  const availableMenus = useMemo(() => {
    const addedMenuIds = menus.map((m) => m.menu_id);
    return preorderMenus.filter((menu) => !addedMenuIds.includes(menu.id));
  }, [preorderMenus, menus]);

  const getMenuName = useCallback(
    (menuId: string): string => {
      const menu = preorderMenus.find((m) => m.id === menuId);
      return menu?.name ?? "Unknown Menu";
    },
    [preorderMenus]
  );

  const updateBookingContext = useCallback(
    (booking: BookingV4) => {
      diaryContext.setDiaryContext({
        selectedBookingV4: booking
      });
    },
    [diaryContext]
  );

  const handleAddMenu = useCallback(
    async (menuId: string) => {
      if (!selectedBookingV4) return;

      setIsSaving(true);
      try {
        const result = await addCustomerPreorderMenu(
          selectedBookingV4,
          menuId,
          venueConfig?.preorders_close_days
        );

        if (result.booking) {
          updateBookingContext(result.booking);
          notificationsContext.addNotification({
            heading: "Menu added successfully",
            autoClose: 3000,
            toastType: "success"
          });
        } else if (result.error) {
          notificationsContext.addNotification({
            heading: result.error,
            autoClose: 4000,
            toastType: "error"
          });
        }
      } catch {
        notificationsContext.addNotification({
          heading: "Failed to add menu",
          autoClose: 4000,
          toastType: "error"
        });
      } finally {
        setIsSaving(false);
      }
    },
    [selectedBookingV4, venueConfig, updateBookingContext, notificationsContext]
  );

  const handleRemoveMenu = useCallback(
    async (menuIndex: number) => {
      if (!selectedBookingV4) return;

      setIsSaving(true);
      try {
        const result = await removeCustomerPreorderMenu(
          selectedBookingV4,
          menuIndex
        );

        if (result.booking) {
          updateBookingContext(result.booking);
          notificationsContext.addNotification({
            heading: "Menu removed successfully",
            autoClose: 3000,
            toastType: "success"
          });
        } else if (result.error) {
          notificationsContext.addNotification({
            heading: result.error,
            autoClose: 4000,
            toastType: "error"
          });
        }
      } catch {
        notificationsContext.addNotification({
          heading: "Failed to remove menu",
          autoClose: 4000,
          toastType: "error"
        });
      } finally {
        setIsSaving(false);
      }
    },
    [selectedBookingV4, updateBookingContext, notificationsContext]
  );

  const handleUpdateMenu = useCallback(
    async (menuIndex: number, menuSettings: Partial<CustomerPreorderMenu>) => {
      if (!selectedBookingV4) return;

      setIsSaving(true);
      try {
        const result = await updateCustomerPreorderMenu(
          selectedBookingV4,
          menuIndex,
          menuSettings
        );

        if (result.booking) {
          updateBookingContext(result.booking);
        } else if (result.error) {
          notificationsContext.addNotification({
            heading: result.error,
            autoClose: 4000,
            toastType: "error"
          });
        }
      } catch {
        notificationsContext.addNotification({
          heading: "Failed to update menu",
          autoClose: 4000,
          toastType: "error"
        });
      } finally {
        setIsSaving(false);
      }
    },
    [selectedBookingV4, updateBookingContext, notificationsContext]
  );

  const handleToggleComplete = useCallback(async () => {
    if (!selectedBookingV4) return;

    setIsSaving(true);
    try {
      const result = await toggleCustomerPreordersComplete(selectedBookingV4);

      if (result.booking) {
        updateBookingContext(result.booking);
        notificationsContext.addNotification({
          heading: `Pre-orders marked as ${result.booking.customer_preorder_settings?.complete ? "complete" : "incomplete"}`,
          autoClose: 3000,
          toastType: "success"
        });
      } else if (result.error) {
        notificationsContext.addNotification({
          heading: result.error,
          autoClose: 4000,
          toastType: "error"
        });
      }
    } catch {
      notificationsContext.addNotification({
        heading: "Failed to update pre-order status",
        autoClose: 4000,
        toastType: "error"
      });
    } finally {
      setIsSaving(false);
    }
  }, [selectedBookingV4, updateBookingContext, notificationsContext]);

  const handleStartEditCloseDate = useCallback(() => {
    const currentCloseDate = customerPreorderSettings?.close_date;
    if (currentCloseDate) {
      setCloseDateValue(new Date(currentCloseDate));
    } else {
      setCloseDateValue(undefined);
    }
    setEditingCloseDate(true);
  }, [customerPreorderSettings]);

  const handleSaveCloseDate = useCallback(async () => {
    if (!selectedBookingV4) return;

    setIsSaving(true);
    try {
      const result = await updateCustomerPreorderSettings(selectedBookingV4, {
        close_date: closeDateValue
      });

      if (result.booking) {
        updateBookingContext(result.booking);
        notificationsContext.addNotification({
          heading: "Close date updated successfully",
          autoClose: 3000,
          toastType: "success"
        });
      } else if (result.error) {
        notificationsContext.addNotification({
          heading: result.error,
          autoClose: 4000,
          toastType: "error"
        });
      }
    } catch {
      notificationsContext.addNotification({
        heading: "Failed to update close date",
        autoClose: 4000,
        toastType: "error"
      });
    } finally {
      setIsSaving(false);
      setEditingCloseDate(false);
    }
  }, [
    selectedBookingV4,
    closeDateValue,
    updateBookingContext,
    notificationsContext
  ]);

  const handleStartEditServiceCharge = useCallback(() => {
    setServiceChargeValue(
      String(customerPreorderSettings?.service_charge_percentage ?? 0)
    );
    setEditingServiceCharge(true);
  }, [customerPreorderSettings]);

  const handleSaveServiceCharge = useCallback(async () => {
    if (!selectedBookingV4) return;

    setIsSaving(true);
    try {
      const result = await updateCustomerPreorderSettings(selectedBookingV4, {
        service_charge_percentage: parseServiceChargeValue(serviceChargeValue)
      });

      if (result.booking) {
        updateBookingContext(result.booking);
        notificationsContext.addNotification({
          heading: "Service charge updated successfully",
          autoClose: 3000,
          toastType: "success"
        });
      } else if (result.error) {
        notificationsContext.addNotification({
          heading: result.error,
          autoClose: 4000,
          toastType: "error"
        });
      }
    } catch {
      notificationsContext.addNotification({
        heading: "Failed to update service charge",
        autoClose: 4000,
        toastType: "error"
      });
    } finally {
      setIsSaving(false);
      setEditingServiceCharge(false);
    }
  }, [
    selectedBookingV4,
    serviceChargeValue,
    updateBookingContext,
    notificationsContext
  ]);

  const formatCloseDate = (date?: Date): string => {
    if (!date) return "Not set";
    const d = new Date(date);
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  return (
    <>
      {showSendEmail && (
        <SendEmail
          handleClose={() => setShowSendEmail(false)}
          handleSent={() => setShowSendEmail(false)}
          initialEmailType="preorder"
        />
      )}
      <section className="py-4 px-4 pb-20 border-t border-border">
        <button
          type="button"
          className="flex items-center justify-between w-full text-left"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          aria-controls="customer-preorders-content"
        >
          <h3 className="text-sm font-semibold">Customer Pre-Orders</h3>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          )}
        </button>

        {isExpanded && (
          <div id="customer-preorders-content" className="mt-4 space-y-4">
            <div className="space-y-3">
              <h4 className="text-sm font-medium">Menus</h4>
              {preorderMenus.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-4 border border-border rounded-lg bg-muted/30">
                  No menus configured for this venue.
                </div>
              ) : (
                <div className="border border-border rounded-lg p-3 bg-muted/30">
                  <AddMenuDropdown
                    availableMenus={availableMenus}
                    onAddMenu={handleAddMenu}
                    isSaving={isSaving}
                  />
                </div>
              )}
              {menus.map((menu, index) => (
                <CustomerPreorderMenuRow
                  key={menu.menu_id}
                  menu={menu}
                  menuName={getMenuName(menu.menu_id)}
                  menuIndex={index}
                  onRemove={handleRemoveMenu}
                  onUpdate={handleUpdateMenu}
                  isSaving={isSaving}
                  numPeople={selectedBookingV4?.num_people}
                  currencySymbol={venueConfig?.currency ?? "£"}
                />
              ))}
            </div>

            {menus.length > 0 && (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="preorders-complete"
                      checked={isComplete}
                      onCheckedChange={handleToggleComplete}
                      disabled={isSaving}
                      aria-label="Mark pre-orders as complete"
                    />
                    <Label htmlFor="preorders-complete" className="text-sm">
                      Pre-orders complete
                    </Label>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="border border-border rounded-lg p-3 bg-muted/30">
                    <div className="text-xs text-muted-foreground mb-1">
                      Close Date
                    </div>
                    {editingCloseDate ? (
                      <div className="flex flex-col gap-2">
                        <EDSDatePicker
                          date={closeDateValue}
                          onSelect={setCloseDateValue}
                          placeholder="Select close date"
                          disabled={isSaving}
                          id="preorder-close-date"
                        />
                        <div className="flex gap-2">
                          <EDSButton
                            variant="default"
                            size="sm"
                            onClick={handleSaveCloseDate}
                            disabled={isSaving}
                          >
                            Save
                          </EDSButton>
                          <EDSButton
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingCloseDate(false)}
                            disabled={isSaving}
                          >
                            Cancel
                          </EDSButton>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                          {formatCloseDate(
                            customerPreorderSettings?.close_date
                          )}
                        </span>
                        <EDSButton
                          variant="outline"
                          size="sm"
                          onClick={handleStartEditCloseDate}
                          disabled={isSaving}
                        >
                          Edit
                        </EDSButton>
                      </div>
                    )}
                  </div>

                  <div className="border border-border rounded-lg p-3 bg-muted/30">
                    <div className="text-xs text-muted-foreground mb-1">
                      Service Charge
                    </div>
                    {editingServiceCharge ? (
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={serviceChargeValue}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (isValidServiceChargeInput(val)) {
                                setServiceChargeValue(val);
                              }
                            }}
                            className="w-20"
                            aria-label="Service charge percentage"
                            autoComplete="none"
                          />
                          <span className="text-sm">%</span>
                        </div>
                        <div className="flex gap-2">
                          <EDSButton
                            variant="default"
                            size="sm"
                            onClick={handleSaveServiceCharge}
                            disabled={isSaving}
                          >
                            Save
                          </EDSButton>
                          <EDSButton
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingServiceCharge(false)}
                            disabled={isSaving}
                          >
                            Cancel
                          </EDSButton>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                          {customerPreorderSettings?.service_charge_percentage ??
                            0}
                          %
                        </span>
                        <EDSButton
                          variant="outline"
                          size="sm"
                          onClick={handleStartEditServiceCharge}
                          disabled={isSaving}
                        >
                          Edit
                        </EDSButton>
                      </div>
                    )}
                  </div>
                </div>

                {isSaving && (
                  <Alert variant="warning" className="mt-4">
                    <Loader2
                      className="h-4 w-4 animate-spin"
                      aria-hidden="true"
                    />
                    <AlertDescription>
                      Saving changes... Please wait before sharing links or
                      sending emails.
                    </AlertDescription>
                  </Alert>
                )}

                <CustomerPreorderLinks
                  bookingId={selectedBookingV4?._id ?? ""}
                />

                {showDisabledReasons &&
                  sendEmailDisabled &&
                  sendEmailDisabledReasons.length > 0 && (
                    <Alert variant="warning" className="mt-4">
                      <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                      <AlertDescription>
                        <span className="font-medium">
                          Cannot send email. Please resolve the following:
                        </span>
                        <ul className="list-disc list-inside mt-2 space-y-1">
                          {sendEmailDisabledReasons.map((reason) => (
                            <li key={reason}>{reason}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                <EDSButton
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (sendEmailDisabled && !isSaving) {
                      setShowDisabledReasons(true);
                    } else if (!isSaving) {
                      setShowSendEmail(true);
                    }
                  }}
                  disabled={isSaving}
                  className="w-full min-h-[44px]"
                  aria-label={
                    sendEmailDisabled
                      ? "Show reasons why email cannot be sent"
                      : "Send pre-orders email to lead booker"
                  }
                >
                  {isSaving ? (
                    <Loader2
                      className="h-4 w-4 mr-2 animate-spin"
                      aria-hidden="true"
                    />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" aria-hidden="true" />
                  )}
                  {isSaving ? "Saving..." : "Send Pre-Orders Email"}
                </EDSButton>
              </>
            )}
          </div>
        )}
      </section>
    </>
  );
};

export default CustomerPreordersSection;
